/* eslint-disable jsx-a11y/anchor-is-valid */
import {
  useOrganizationsCustomerCreate,
  useOrganizationsCustomerCreationEntitiesQuery,
  useOrganizationsCustomerUploadLogo,
} from "@/lib/api/requests/organizations.requests";
import { Button } from "@/ui-components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/ui-components/ui/form";
import { ImageUpload } from "@/ui-components/ui/image-upload";
import { Input } from "@/ui-components/ui/input";
import { zodResolver } from "@hookform/resolvers/zod";
import { Card, CardContent } from "@/ui-components/ui/card";
import { Loader2, Palette, Shield } from "lucide-react";
import { useContext, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "@/lib/toast";
import z from "zod";
import { Context } from "@/util-components/root/context";
import confetti from "canvas-confetti";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { useFormConflictError } from "@/lib/hooks/use-form-conflict-error";
import { trackMetaCustomEvent } from "@/lib/meta-pixel/meta-pixel";

const ColorPickerInput = ({ 
  value, 
  onChange, 
  label, 
  disabled 
}: { 
  value?: string; 
  onChange: (value: string) => void; 
  label: string;
  disabled?: boolean;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleDivClick = () => {
    if (!disabled && inputRef.current) {
      inputRef.current.click();
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <FormLabel>{label}</FormLabel>
      <div className="flex gap-3 items-center">
        <div 
          className={`w-12 h-10 rounded-lg border shadow-sm cursor-pointer flex-shrink-0 relative overflow-hidden transition-all hover:scale-105 active:scale-95 ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          style={{ backgroundColor: value || '#ffffff' }}
          onClick={handleDivClick}
        >
          <input
            ref={inputRef}
            type="color"
            value={value || '#ffffff'}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            className="absolute opacity-0 w-full h-full top-0 left-0 cursor-pointer"
          />
        </div>
        <Input 
          value={value || ''} 
          onChange={(e) => onChange(e.target.value)} 
          placeholder="#FFFFFF" 
          disabled={disabled}
          className="font-mono uppercase h-10"
          maxLength={7}
        />
      </div>
    </div>
  );
};

export interface CreateTeamProps {
  initialConflictError?: { message: string, field: "name" | "primaryColor" | "secondaryColor" | "image" };
}

const CreateTeam = ({ initialConflictError }: CreateTeamProps) => {
  const { accessToken, user, getAccessToken } = useContext(Context);
  const { data: creationEntities } = useOrganizationsCustomerCreationEntitiesQuery({ enabled: !!accessToken && !user?.organizationName });
  const { mutateAsync: createOrganisation } = useOrganizationsCustomerCreate(
    undefined,
    {
      skipGlobalErrorHandler: true,
      throwOnError: true,
    }
  );
  const { mutateAsync: uploadLogo } = useOrganizationsCustomerUploadLogo({
    throwOnError: true,
  });
  const [isLoading, setIsLoading] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const createInvitationSchema = z.object({
    name: z.string().min(1, {
      message: "Name des Vereins ist erforderlich",
    }),
    primaryColor: z.string().optional(),
    secondaryColor: z.string().optional(),
    image: z.instanceof(File).optional(),
  });

  type CreateInvitationSchema = z.infer<typeof createInvitationSchema>;

  const form = useForm<CreateInvitationSchema>({
    resolver: zodResolver(createInvitationSchema),
    defaultValues: {
      name: creationEntities?.organizationName || "",
      primaryColor: "#000000",
      secondaryColor: "#ffffff",
      image: undefined,
    },
  });

  const { handleConflictError } = useFormConflictError(form, { default: "name" });

  useEffect(() => {
    if (initialConflictError) {
      form.setError(initialConflictError.field, { 
        type: "manual", 
        message: initialConflictError.message 
      });
    }
  }, [initialConflictError, form]);

  const onSubmit = async (data: CreateInvitationSchema) => {
    setIsLoading(true);

    try {
      await createOrganisation({
        name: data.name,
        primaryColor: data.primaryColor,
        secondaryColor: data.secondaryColor,
      });

      trackMetaCustomEvent("OrganizationCreated", {
        organization_name: data.name,
      });

      if (data.image) {
        try {
          await getAccessToken({ forceRefresh: true });
          await uploadLogo({ file: data.image });
        } catch (firstUploadError) {
          console.error("Initial logo upload failed, retrying once", firstUploadError);

          try {
            await getAccessToken({ forceRefresh: true });
            await uploadLogo({ file: data.image });
          } catch (secondUploadError) {
            console.error("Logo upload retry failed", secondUploadError);
            toast.error("Logo-Upload fehlgeschlagen", { error: secondUploadError });
          }
        }
      }

      toast.success("Verein wurde erfolgreich erstellt");
      
      // Celebrate with confetti
      confetti({
        particleCount: 150,
        spread: 70,
        origin: { y: 0.6 },
        colors: [data.primaryColor || '#2563eb', data.secondaryColor || '#93c5fd'],
      });

      // Wait for confetti to finish + buffer (approx 3 seconds)
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      // Redirect after success
      // Refetch user info to ensure redirect logic works correctly without reload
      await queryClient.refetchQueries({ queryKey: ["user-profile", "userinfo"] });
      
      setIsLoading(false);
      navigate("/");
    } catch (error) {
      handleConflictError(error as any, "Verein konnte nicht erstellt werden");
      console.error("Creation error:", error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (creationEntities) {
      form.setValue("name", creationEntities.organizationName);
    }
  }, [creationEntities, form]);

  return (
    <div className="h-screen w-full bg-slate-50/30 flex flex-col items-center py-12 px-4 sm:px-8 overflow-y-auto">
      <div className="w-full max-w-3xl space-y-10 animate-in fade-in duration-700 slide-in-from-bottom-4">
        
        {/* Header Section */}
        <div className="flex flex-col items-center text-center gap-6">
           <img src="/logo.png" alt="SportyPost Logo" className="h-10 md:h-12 object-contain opacity-80" />
        </div>

        <Card className="border-none shadow-xl shadow-slate-200/40 bg-white ring-1 ring-slate-200/60 overflow-hidden rounded-2xl">
          <CardContent className="p-0">
            {/* Progress / Step Indicator could go here if multi-step */}
            
            <div className="p-8 md:p-10 space-y-8">
              
              <div className="flex items-start gap-4 pb-6 border-b border-slate-100">
                <div className="p-3 bg-blue-50 rounded-xl text-blue-600">
                  <Shield className="w-6 h-6" />
                </div>
                <div className="space-y-1">
                  <h2 className="text-xl font-semibold text-slate-900">Vereinseinrichtung</h2>
                  <p className="text-slate-500 text-sm">Legen Sie das Fundament für Ihren professionellen Markenauftritt.</p>
                </div>
              </div>

              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
                    
                    {/* Left Column: Input Fields */}
                    <div className="space-y-8">
                      <FormField
                        control={form.control}
                        name="name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-base font-medium text-slate-700">Wie heißt Ihr Verein?</FormLabel>
                            <FormControl>
                              <Input 
                                placeholder="z.B. FC Musterstadt 1900" 
                                {...field} 
                                disabled={isLoading || !accessToken} 
                                className="h-11 text-base shadow-sm border-slate-200 bg-slate-50/50 focus:bg-white transition-all focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 rounded-xl" 
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <div className="space-y-4">
                         <div className="flex items-center gap-2">
                           <Palette className="w-4 h-4 text-slate-400" />
                           <span className="text-sm font-medium text-slate-600 uppercase tracking-wider">Vereinsfarben</span>
                         </div>
                         <div className="grid grid-cols-2 gap-4">
                          <FormField
                            control={form.control}
                            name="primaryColor"
                            render={({ field }) => (
                              <FormItem>
                                <FormControl>
                                  <ColorPickerInput 
                                    label="Primär" 
                                    value={field.value} 
                                    onChange={field.onChange} 
                                    disabled={isLoading || !accessToken} 
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="secondaryColor"
                            render={({ field }) => (
                              <FormItem>
                                <FormControl>
                                  <ColorPickerInput 
                                    label="Sekundär" 
                                    value={field.value} 
                                    onChange={field.onChange} 
                                    disabled={isLoading || !accessToken} 
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Right Column: Logo Upload */}
                    <div className="flex flex-col">
                      <FormField
                        control={form.control}
                        name="image"
                        render={({ field }) => (
                          <FormItem className="flex-1 flex flex-col h-full">
                            <FormLabel className="text-base font-medium text-slate-700 mb-3">Vereinswappen</FormLabel>
                            <FormControl>
                              <div className={`flex-1 min-h-[240px] rounded-xl overflow-hidden transition-colors ${
                                field.value 
                                  ? "border-none bg-transparent" 
                                  : "border-2 border-dashed border-slate-200 bg-slate-50/50 hover:bg-slate-50"
                              }`}>
                                <ImageUpload
                                  value={field.value}
                                  onChange={field.onChange}
                                  disabled={isLoading || !accessToken}
                                  recommendedAspectRatio={null}
                                  aspectRatio="1:1"
                                  imageLabel="Logo hochladen"
                                  className="w-full h-full min-h-[240px] border-none"
                                />
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>

                  <div className="pt-8 border-t border-slate-100 flex flex-col-reverse sm:flex-row items-center justify-between gap-6">
                    <p className="text-sm text-slate-400 text-center sm:text-left">
                      Mit dem Erstellen akzeptieren Sie unsere <a href="#" className="text-slate-600 hover:text-blue-600 font-medium transition-colors">AGB</a>.
                    </p>
                    
                    <Button 
                      type="submit" 
                      disabled={isLoading || !accessToken} 
                      className="w-full sm:w-auto px-8 h-11 text-base font-medium shadow-lg shadow-blue-500/25 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-all hover:scale-[1.02] active:scale-[0.98]"
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                          Richte Verein ein...
                        </>
                      ) : (
                        "Verein erstellen & Starten"
                      )}
                    </Button>
                  </div>
                </form>
              </Form>
            </div>
          </CardContent>
        </Card>
        
        <div className="grid grid-cols-3 gap-4 opacity-60 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">
           <div className="flex flex-col items-center gap-2">
             <Shield className="w-5 h-5 text-slate-400" />
             <span>Sicher & DSGVO</span>
           </div>
           <div className="flex flex-col items-center gap-2">
             <Palette className="w-5 h-5 text-slate-400" />
             <span>Auto-Design</span>
           </div>
           <div className="flex flex-col items-center gap-2">
             <Loader2 className="w-5 h-5 text-slate-400" />
             <span>Zeitersparnis</span>
           </div>
        </div>

      </div>
    </div>
  );
};

export default CreateTeam;
